import React, { useState } from 'react';
import {
  Box,
  Fab,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  IconButton,
  Tooltip,
  Zoom,
  CircularProgress,
  Collapse,
  Button,
  Chip,
} from '@mui/material';
import { keyframes } from '@mui/system';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { useVoiceNavigation, VoiceNavigationState } from './VoiceNavigationProvider';

// ============================================================================
// Animations
// ============================================================================

const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(244, 67, 54, 0.7);
  }
  70% {
    box-shadow: 0 0 0 15px rgba(244, 67, 54, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(244, 67, 54, 0);
  }
`;

const ripple = keyframes`
  0% {
    transform: scale(1);
    opacity: 1;
  }
  100% {
    transform: scale(1.5);
    opacity: 0;
  }
`;

// ============================================================================
// Helper Components
// ============================================================================

interface StatusIndicatorProps {
  state: VoiceNavigationState;
}

const StatusIndicator: React.FC<StatusIndicatorProps> = ({ state }) => {
  const getStatusConfig = () => {
    switch (state) {
    case 'wake-word-listening':
      return { color: '#9c27b0', icon: <MicIcon />, label: 'Say "Hey Vidai"' };
    case 'listening':
      return { color: '#f44336', icon: <MicIcon />, label: 'Listening...' };
    case 'processing':
      return { color: '#ff9800', icon: <CircularProgress size={20} sx={{ color: '#fff' }} />, label: 'Processing...' };
    case 'success':
      return { color: '#4caf50', icon: <CheckCircleIcon />, label: 'Success!' };
    case 'error':
      return { color: '#f44336', icon: <ErrorIcon />, label: 'Error' };
    case 'not-found':
      return { color: '#ff9800', icon: <HelpOutlineIcon />, label: 'Not Found' };
    case 'confirming':
      return { color: '#2196f3', icon: <HelpOutlineIcon />, label: 'Confirm?' };
    default:
      return { color: '#1976d2', icon: <MicIcon />, label: 'Tap to speak' };
    }
  };

  const config = getStatusConfig();
  return (
    <Chip
      icon={config.icon as React.ReactElement}
      label={config.label}
      size="small"
      sx={{
        backgroundColor: config.color,
        color: '#fff',
        '& .MuiChip-icon': { color: '#fff' },
      }}
    />
  );
};

// ============================================================================
// Main Component
// ============================================================================

interface FloatingVoiceButtonProps {
  /** Position of the floating button on screen */
  position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
  /** Offset from the edge of the screen in pixels */
  offset?: { x: number; y: number };
}

/**
 * Floating action button for voice navigation.
 * 
 * Displays a floating microphone button that expands to show a voice
 * navigation panel when activated. Shows current state, transcripts,
 * matched commands, and navigation suggestions.
 * 
 * @param props - Component properties
 * @param props.position - Screen corner position (default: 'bottom-right')
 * @param props.offset - Pixel offset from screen edge (default: { x: 20, y: 20 })
 * 
 * @example
 * ```tsx
 * <FloatingVoiceButton position="bottom-right" offset={{ x: 24, y: 24 }} />
 * ```
 */
const FloatingVoiceButton: React.FC<FloatingVoiceButtonProps> = ({
  position = 'bottom-right',
  offset = { x: 20, y: 20 },
}) => {
  const [expanded, setExpanded] = useState(false);
  
  const {
    state,
    isSupported,
    transcript,
    interimTranscript,
    error,
    matchedCommand,
    suggestions,
    startListening,
    stopListening,
    confirmNavigation,
    cancelNavigation,
    navigateToSuggestion,
    reset,
  } = useVoiceNavigation();

  // Position styles
  const getPositionStyles = () => {
    const styles: React.CSSProperties = { position: 'fixed', zIndex: 1300 };
    switch (position) {
    case 'bottom-right':
      styles.bottom = offset.y;
      styles.right = offset.x;
      break;
    case 'bottom-left':
      styles.bottom = offset.y;
      styles.left = offset.x;
      break;
    case 'top-right':
      styles.top = offset.y;
      styles.right = offset.x;
      break;
    case 'top-left':
      styles.top = offset.y;
      styles.left = offset.x;
      break;
    }
    return styles;
  };

  // Get fab color based on state
  const getFabColor = (): 'primary' | 'secondary' | 'error' | 'success' | 'warning' => {
    switch (state) {
    case 'wake-word-listening':
      return 'secondary';
    case 'listening':
      return 'error';
    case 'processing':
      return 'warning';
    case 'success':
      return 'success';
    case 'error':
      return 'error';
    case 'confirming':
      return 'primary';
    case 'not-found':
      return 'warning';
    default:
      return 'primary';
    }
  };

  // Handle fab click
  const handleFabClick = () => {
    if (state === 'listening') {
      stopListening();
    } else if (state === 'idle' || state === 'error' || state === 'not-found' || state === 'wake-word-listening') {
      startListening();
      setExpanded(true);
    } else {
      setExpanded(!expanded);
    }
  };

  // Handle close
  const handleClose = () => {
    reset();
    setExpanded(false);
  };

  // Determine if panel should show
  const showPanel = expanded || state === 'listening' || state === 'processing' || 
                    state === 'confirming' || state === 'not-found' || 
                    (state === 'success' && !!matchedCommand) ||
                    (state === 'error' && !!error);

  // Don't render if not supported
  if (!isSupported) {
    return null;
  }

  const displayText = transcript || interimTranscript;

  return (
    <Box sx={getPositionStyles()}>
      {/* Expanded Panel */}
      <Collapse in={showPanel} sx={{ mb: 2 }}>
        <Paper
          elevation={8}
          sx={{
            width: 320,
            maxWidth: 'calc(100vw - 48px)',
            borderRadius: 3,
            overflow: 'hidden',
            mb: 2,
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2,
              py: 1.5,
              backgroundColor: 'primary.main',
              color: 'white',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <MicIcon fontSize="small" />
              <Typography variant="subtitle2" fontWeight={600}>
                Voice Navigation
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={handleClose}
              sx={{ color: 'white' }}
              aria-label="Close voice navigation"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Content */}
          <Box sx={{ p: 2 }}>
            {/* Status */}
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
              <StatusIndicator state={state} />
            </Box>

            {/* Transcript Display */}
            {displayText && (
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  mb: 2,
                  backgroundColor: '#f5f5f5',
                  borderRadius: 2,
                }}
                role="status"
                aria-live="polite"
                aria-label="Voice transcript"
              >
                <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                  &ldquo;{displayText}&rdquo;
                </Typography>
              </Paper>
            )}

            {/* Matched Command */}
            {matchedCommand && state !== 'not-found' && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                  Navigating to:
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    p: 1.5,
                    backgroundColor: state === 'success' ? '#e8f5e9' : '#e3f2fd',
                    borderRadius: 2,
                  }}
                >
                  <NavigateNextIcon color={state === 'success' ? 'success' : 'primary'} />
                  <Typography variant="body2" fontWeight={500}>
                    {matchedCommand.label}
                  </Typography>
                  {matchedCommand.confidence < 1 && (
                    <Chip
                      label={`${Math.round(matchedCommand.confidence * 100)}%`}
                      size="small"
                      variant="outlined"
                      sx={{ ml: 'auto' }}
                    />
                  )}
                </Box>
              </Box>
            )}

            {/* Confirmation Buttons */}
            {state === 'confirming' && matchedCommand && (
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={confirmNavigation}
                >
                  Confirm
                </Button>
                <Button
                  variant="outlined"
                  color="inherit"
                  size="small"
                  onClick={cancelNavigation}
                >
                  Cancel
                </Button>
              </Box>
            )}

            {/* Error Display */}
            {error && (
              <Box
                sx={{
                  p: 1.5,
                  backgroundColor: '#ffebee',
                  borderRadius: 2,
                  mb: 2,
                }}
              >
                <Typography variant="body2" color="error">
                  {error}
                </Typography>
              </Box>
            )}

            {/* Suggestions */}
            {suggestions.length > 0 && state === 'not-found' && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  Did you mean:
                </Typography>
                <List dense sx={{ p: 0 }}>
                  {suggestions.map((suggestion, index) => (
                    <ListItem key={index} disablePadding>
                      <ListItemButton
                        onClick={() => navigateToSuggestion(suggestion)}
                        sx={{
                          borderRadius: 1,
                          mb: 0.5,
                          '&:hover': { backgroundColor: '#e3f2fd' },
                        }}
                      >
                        <ListItemText
                          primary={suggestion.label}
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                        <NavigateNextIcon fontSize="small" color="action" />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            {/* Help Text */}
            {state === 'listening' && !displayText && (
              <Typography variant="body2" color="text.secondary" textAlign="center">
                Say a command like &ldquo;Open billing&rdquo; or &ldquo;Go to registration&rdquo;
              </Typography>
            )}
          </Box>
        </Paper>
      </Collapse>

      {/* Floating Action Button */}
      <Tooltip
        title={state === 'listening' ? 'Stop listening' : 'Tap to speak command'}
        placement="left"
        TransitionComponent={Zoom}
      >
        <Box sx={{ position: 'relative' }}>
          {/* Ripple effect when listening */}
          {state === 'listening' && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                borderRadius: '50%',
                backgroundColor: 'error.main',
                animation: `${ripple} 1.5s ease-out infinite`,
              }}
            />
          )}
          <Fab
            color={getFabColor()}
            onClick={handleFabClick}
            aria-label={
              state === 'listening' 
                ? 'Stop voice navigation' 
                : state === 'processing' 
                  ? 'Processing voice command' 
                  : 'Start voice navigation'
            }
            aria-pressed={state === 'listening'}
            sx={{
              position: 'relative',
              ...(state === 'listening' && {
                animation: `${pulse} 1.5s ease-in-out infinite`,
              }),
            }}
          >
            {state === 'processing' ? (
              <CircularProgress size={24} sx={{ color: 'white' }} />
            ) : state === 'listening' ? (
              <MicOffIcon />
            ) : state === 'success' ? (
              <CheckCircleIcon />
            ) : state === 'error' ? (
              <ErrorIcon />
            ) : (
              <MicIcon />
            )}
          </Fab>
        </Box>
      </Tooltip>
    </Box>
  );
};

export default FloatingVoiceButton;
